import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Space,
  Modal,
  Select,
  Switch,
  Upload,
  Tag,
  message,
  Row,
  Col,
  Avatar,
  Tooltip,
  Dropdown,
  Grid
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  GlobalOutlined,
  UploadOutlined,
  StarOutlined,
  TrophyOutlined,
  MailOutlined,
  PhoneOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MoreOutlined
} from '@ant-design/icons';
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { useTranslation } from "react-i18next";
import EntityCardList from "./EntityCardList";
const { TextArea } = Input;
const { Option } = Select;

const SpeakerManagement = ({
  eventId,
  speakers: initialSpeakers,
  onUpdate,
  isCreateMode = false,
  onSpeakersChange,
}) => {
  // `=== false` so the first render (before antd reports breakpoints) defaults to desktop.
  const isMobile = Grid.useBreakpoint().md === false;
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSpeaker, setEditingSpeaker] = useState(null);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);
  const [speakers, setSpeakers] = useState(initialSpeakers || []);
  const [checkIns, setCheckIns] = useState([]); // <--- NEW STATE

  const hasValidEventId =
    typeof eventId === "string" &&
    eventId.trim() !== "" &&
    eventId !== "undefined" &&
    eventId !== "null";
  const canPersistToBackend = !isCreateMode && hasValidEventId;

  useEffect(() => {
    const fetchCheckIns = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/CheckIn/event/${eventId}`);
        if (response.data && response.data.items) {
          setCheckIns(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching check-ins:", error);
      }
    };
    if (canPersistToBackend) fetchCheckIns();
  }, [canPersistToBackend, eventId]);

  useEffect(() => {
    setSpeakers(initialSpeakers || []);
  }, [initialSpeakers]);


  const handleAddSpeaker = () => {
    setEditingSpeaker(null);
    form.resetFields();
    setModalVisible(true);
  };

  const saveSpeakersToBackend = async (updatedSpeakers) => {
    if (!canPersistToBackend) {
      throw new Error("Event must be created before saving speakers to backend");
    }

    try {
      const response = await axios.get(`${BASE_URL}/Event/${eventId}`);
      const eventData = response.data;

      await axios.put(
        `${BASE_URL}/Event/${eventId}`,
        { ...eventData, speakers: updatedSpeakers }
      );

      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error saving speakers:', error);
      throw error;
    }
  };

  const handleEditSpeaker = (speaker) => {
    setEditingSpeaker(speaker);
    form.setFieldsValue(speaker);
    setModalVisible(true);
  };

  const handleDeleteSpeaker = (speakerId) => {
    Modal.confirm({
      title: t('eventSections.speakers.delete'),
      content: t('eventSections.speakers.deleteConfirm'),
      okText: t('eventSections.common.delete'),
      okType: 'danger',
      onOk: async () => {
        try {
          const updatedSpeakers = speakers.filter(s => s.id !== speakerId);

          if (!canPersistToBackend) {
            setSpeakers(updatedSpeakers);
            if (onSpeakersChange) onSpeakersChange(updatedSpeakers);
            message.success(t('eventSections.speakers.deleted'));
            return;
          }

          await saveSpeakersToBackend(updatedSpeakers);
          setSpeakers(updatedSpeakers);
          if (onSpeakersChange) onSpeakersChange(updatedSpeakers);
          message.success(t('eventSections.speakers.deleted'));
        } catch (error) {
          message.error(t('eventSections.speakers.deleteFailed'));
        }
      }
    });
  };


  const handleSendCard = async (speaker) => {
    if (!canPersistToBackend) {
      message.warning(t('eventSections.common.createEventFirst'));
      return;
    }

    try {
      message.loading({ content: t('eventSections.speakers.sendingWallet'), key: 'sendCard' });
      const response = await axios.post(`${BASE_URL}/EventWallet/send-card`, {
        type: "speaker",
        eventId: eventId,
        speakerId: speaker.id,
        email: speaker.email
      });
      if (response.data.success) {
        message.success({
          content: t('eventSections.common.walletSent', { email: speaker.email }),
          key: 'sendCard',
          duration: 4
        });
      }
    } catch (error) {
      message.error({
        content: error.response?.data?.message || t('eventSections.common.walletSendFailed'),
        key: 'sendCard'
      });
    }
  };

  // --- REMOVED: Company logo upload logic (now managed in company settings) ---

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const speakerId = editingSpeaker?.id || crypto.randomUUID();

      const speakerData = {
        id: speakerId,
        name: values.name,
        title: values.title,
        company: values.company,
        bio: values.bio,
        photoUrl: values.photoUrl,
        email: values.email,
        phone: values.phone,
        linkedInUrl: values.linkedInUrl,
        twitterUrl: values.twitterUrl,
        websiteUrl: values.websiteUrl,
        expertise: values.expertise || [],
        isFeatured: values.isFeatured || false,
        isKeynote: values.isKeynote || false,
        arabicName: values.arabicName,
        arabicTitle: values.arabicTitle,
        arabicCompany: values.arabicCompany,
        arabicBio: values.arabicBio,
        order: values.order || speakers.length,
        companyIconUrl: editingSpeaker?.companyIconUrl // Keep existing (no longer updated)
      };

      let updatedSpeakers;
      if (editingSpeaker) {
        updatedSpeakers = speakers.map(s => s.id === editingSpeaker.id ? speakerData : s);
      } else {
        updatedSpeakers = [...speakers, speakerData];
      }

      if (!canPersistToBackend) {
        setSpeakers(updatedSpeakers);
        if (onSpeakersChange) onSpeakersChange(updatedSpeakers);
        message.success(editingSpeaker ? t('eventSections.speakers.updated') : t('eventSections.speakers.added'));
        setModalVisible(false);
        form.resetFields();
        return;
      }

      await saveSpeakersToBackend(updatedSpeakers);
      setSpeakers(updatedSpeakers);
      if (onSpeakersChange) onSpeakersChange(updatedSpeakers);
      message.success(editingSpeaker ? t('eventSections.speakers.updated') : t('eventSections.speakers.added'));
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Error saving speaker:', error);
      message.error(t('eventSections.speakers.saveFailed'));
    }
  };

  const getMenuItems = (record) => [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: t('eventSections.speakers.edit'),
          onClick: () => handleEditSpeaker(record),
        },
        {
          key: 'send',
          icon: <MailOutlined />,
          label: t('eventSections.common.sendWalletPass'),
          onClick: () => handleSendCard(record),
        },
        {
          type: 'divider',
        },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: t('eventSections.speakers.delete'),
          danger: true,
          onClick: () => handleDeleteSpeaker(record.id),
        },
      ];

  const columns = [
    {
      title: t('eventSections.common.order'),
      dataIndex: 'order',
      width: 70,
      sorter: (a, b) => a.order - b.order,
      responsive: ['md'],
    },
    {
      title: t('eventSections.speakers.columns.speaker'),
      key: 'speaker',
      width: 250,
      ellipsis: true,
      render: (_, record) => (
        <Space>
          <Avatar
            src={record.photoUrl}
            icon={<UserOutlined />}
            size={48}
          />
          <div>
            <div className="font-medium">{record.name}</div>
            <div className="text-gray-500 text-sm">{record.title}</div>
            <div className="text-gray-400 text-xs">{record.company}</div>
          </div>
        </Space>
      )
    },
    {
      title: t('eventSections.speakers.columns.expertise'),
      dataIndex: 'expertise',
      key: 'expertise',
      width: 200,
      ellipsis: true,
      render: (expertise) => (
        <>
          {expertise?.slice(0, 2).map((exp, idx) => (
            <Tag key={idx} color="blue">{exp}</Tag>
          ))}
          {expertise?.length > 2 && <Tag>+{expertise.length - 2}</Tag>}
        </>
      )
    },
    {
      title: t('eventSections.common.contact'),
      key: 'contact',
      width: 220,
      ellipsis: true,
      render: (_, record) => (
        <div>
          {record.email && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
              <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              <span style={{ fontSize: '12px' }}>{record.email}</span>
            </div>
          )}
          {record.phone && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
              <span style={{ fontSize: '12px' }}>{record.phone}</span>
            </div>
          )}
        </div>
      )
    },
    {
      title: t('eventSections.speakers.columns.social'),
      key: 'social',
      width: 150,
      render: (_, record) => (
        <Space>
          {record.linkedInUrl && <LinkedinOutlined style={{ color: '#0077b5' }} />}
          {record.twitterUrl && <TwitterOutlined style={{ color: '#1DA1F2' }} />}
          {record.websiteUrl && <GlobalOutlined />}
        </Space>
      )
    },
    {
      title: t('eventSections.common.checkIn'),
      key: 'checkIn',
      width: 130,
      render: (_, record) => {
        // Match by Registration ID (Speaker ID)
        const isCheckedIn = checkIns.some(c =>
          c.registrationId === record.id
        );
        return isCheckedIn ? (
          <Tag color="green" icon={<CheckCircleOutlined />}>{t('eventSections.common.checkedIn')}</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />}>{t('eventSections.common.notYet')}</Tag>
        );
      }
    },
    {
      title: t('eventSections.common.status'),
      key: 'status',
      width: 120,
      render: (_, record) => (
        <Space direction="horizontal" size={4}>
          {record.isFeatured && <Tag color="gold" icon={<StarOutlined />}>{t('eventSections.common.featured')}</Tag>}
          {record.isKeynote && <Tag color="purple" icon={<TrophyOutlined />}>{t('eventSections.common.keynote')}</Tag>}
        </Space>
      )
    },
    {
      title: t('eventSections.common.actions'),
      key: 'actions',
      fixed: 'right',
      width: 80,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: getMenuItems(record),
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined style={{ fontSize: '18px' }} />} />
        </Dropdown>
      )
    }
  ];

  return (
    <Card
      title={t('eventSections.speakers.title')}
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddSpeaker}
        >
          {t('eventSections.speakers.add')}
        </Button>
      }
    >
      {isMobile ? (
        <EntityCardList
          data={[...speakers].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))}
          avatar={(r) => <Avatar src={r.photoUrl} icon={<UserOutlined />} size={48} style={{ flexShrink: 0 }} />}
          title={(r) => r.name}
          subtitle={(r) => [r.title, r.company].filter(Boolean).join(' · ')}
          tags={(r) => {
            const isCheckedIn = checkIns.some((c) => c.registrationId === r.id);
            return (
              <>
                {isCheckedIn ? (
                  <Tag color="green" icon={<CheckCircleOutlined />}>{t('eventSections.common.checkedIn')}</Tag>
                ) : (
                  <Tag icon={<CloseCircleOutlined />}>{t('eventSections.common.notYet')}</Tag>
                )}
                {r.isFeatured && <Tag color="gold" icon={<StarOutlined />}>{t('eventSections.common.featured')}</Tag>}
                {r.isKeynote && <Tag color="purple" icon={<TrophyOutlined />}>{t('eventSections.common.keynote')}</Tag>}
                {r.expertise?.slice(0, 2).map((exp, idx) => (
                  <Tag key={idx} color="blue">{exp}</Tag>
                ))}
                {r.expertise?.length > 2 && <Tag>+{r.expertise.length - 2}</Tag>}
              </>
            );
          }}
          details={(r) =>
            (r.email || r.phone) ? (
              <>
                {r.email && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><MailOutlined style={{ color: '#1890ff' }} /> {r.email}</span>}
                {r.phone && <span><PhoneOutlined style={{ color: '#1890ff' }} /> {r.phone}</span>}
              </>
            ) : null
          }
          menuItems={getMenuItems}
          onTap={handleEditSpeaker}
          emptyText={t('eventSections.speakers.empty')}
        />
      ) : (
      <Table
        dataSource={speakers}
        columns={columns}
        rowKey="id"
        // tableLayout="fixed"
        scroll={{ x: 1200 }}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        locale={{ emptyText: t('eventSections.speakers.empty') }}
      />
      )}

      <Modal
        title={editingSpeaker ? t('eventSections.speakers.edit') : t('eventSections.speakers.add')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={isMobile ? 'calc(100vw - 16px)' : 800}
        style={isMobile ? { top: 12 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' } } : undefined}
        okText={editingSpeaker ? t('eventSections.common.update') : t('eventSections.common.add')}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} sm={18}>
              <Form.Item
                name="name"
                label={t('eventSections.speakers.form.fullName')}
                rules={[{ required: true, message: t('eventSections.speakers.form.nameRequired') }]}
              >
                <Input placeholder={t('eventSections.speakers.form.namePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name="order"
                label={t('eventSections.common.displayOrder')}
                initialValue={speakers.length}
              >
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="title"
                label={t('eventSections.speakers.form.jobTitle')}
              >
                <Input placeholder={t('eventSections.speakers.form.jobTitlePlaceholder')} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="company"
                label={t('eventSections.speakers.form.company')}
              >
                <Input placeholder={t('eventSections.speakers.form.companyPlaceholder')} />
              </Form.Item>
            </Col>
          </Row>

          {/* <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Company Logo (for Wallet Pass)">
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#e6f7ff',
                  border: '1px solid #91d5ff',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'start'
                }}>
                  <span style={{ marginRight: '8px', fontSize: '16px' }}>â„¹ï¸</span>
                  <div>
                    <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                      Company logos are managed centrally
                    </div>
                    <div style={{ fontSize: '12px', color: '#595959' }}>
                      The company logo for wallet passes is automatically fetched from your company settings.
                      To update it, go to Settings â†’ Company Profile â†’ Company Logo.
                    </div>
                  </div>
                </div>
              </Form.Item>
            </Col>
          </Row> */}

          <Form.Item
            name="bio"
            label={t('eventSections.speakers.form.biography')}
          >
            <TextArea rows={4} placeholder={t('eventSections.speakers.form.biographyPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="photoUrl"
            label={t('eventSections.speakers.form.photoUrl')}
          >
            <Input placeholder="https://example.com/speaker-photo.jpg" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label={t('eventSections.common.email')}
                rules={[{ type: 'email', message: t('eventSections.common.emailInvalid') }]}
              >
                <Input placeholder="speaker@example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label={t('eventSections.common.phone')}
              >
                <Input placeholder="+1 234 567 8900" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="expertise"
            label={t('eventSections.speakers.form.expertise')}
            help={t('eventSections.speakers.form.expertiseHelp')}
          >
            <Select
              mode="tags"
              placeholder={t('eventSections.speakers.form.expertisePlaceholder')}
              tokenSeparators={[',']}
            />
          </Form.Item>

          <Card title={t('eventSections.speakers.form.socialLinks')} size="small" style={{ marginBottom: 16 }}>
            <Form.Item
              name="linkedInUrl"
              label={t('eventSections.speakers.form.linkedIn')}
            >
              <Input
                prefix={<LinkedinOutlined />}
                placeholder="https://linkedin.com/in/username"
              />
            </Form.Item>
            <Form.Item
              name="twitterUrl"
              label={t('eventSections.speakers.form.twitter')}
            >
              <Input
                prefix={<TwitterOutlined />}
                placeholder="https://twitter.com/username"
              />
            </Form.Item>
            <Form.Item
              name="websiteUrl"
              label={t('eventSections.common.website')}
            >
              <Input
                prefix={<GlobalOutlined />}
                placeholder="https://www.example.com"
              />
            </Form.Item>
          </Card>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="isFeatured"
                label={t('eventSections.speakers.form.featuredSpeaker')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="isKeynote"
                label={t('eventSections.speakers.form.keynoteSpeaker')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Card title={t('eventSections.common.arabicTranslation')} size="small">
            <Form.Item
              name="arabicName"
              label={t('eventSections.common.arabicName')}
            >
              <Input placeholder="الاسم بالعربية" style={{ direction: 'rtl' }} />
            </Form.Item>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="arabicTitle"
                  label={t('eventSections.common.arabicTitle')}
                >
                  <Input placeholder="المسمى الوظيفي" style={{ direction: 'rtl' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="arabicCompany"
                  label={t('eventSections.common.arabicCompany')}
                >
                  <Input placeholder="الشركة" style={{ direction: 'rtl' }} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="arabicBio"
              label={t('eventSections.speakers.form.arabicBiography')}
            >
              <TextArea rows={3} placeholder="السيرة الذاتية بالعربية" style={{ direction: 'rtl' }} />
            </Form.Item>
          </Card>
        </Form>
      </Modal>
    </Card>
  );
};

export default SpeakerManagement;
